<script setup>
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';

import { DEV_MODE } from '@/config/dev-mode';
import { CONTENTS, CONTENTS_PAGE_MAP } from './constants';
// store & API
import { useAnalysisStore } from '@/store/modules/analysis';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';
import { addContentLogAPI } from '@/api/logServiceApi';
import { fetchBaseServiceAPI } from '@/api/baseServiceApi';
import { useContentRate } from '@/composables/saju-insight/useContentRate';
import { isObjectEmpty } from '@/composables/functions';
// components
import ContentsList from '@/components/saju-insight/common/ContentsList.vue';
import ContentsBasic01 from './ContentsBasic01.vue';
import ContentsBasic02 from './ContentsBasic02.vue';
import ContentsBasic03 from './ContentsBasic03.vue';
import ContentsBasic04 from './ContentsBasic04.vue';
import ContentsBasic05 from './ContentsBasic05.vue';
import ContentsBasic06 from './ContentsBasic06.vue';
import ContentsBasic07 from './ContentsBasic07.vue';
import ContentsBasic08 from './ContentsBasic08.vue';
import ContentsBasic09 from './ContentsBasic09.vue';

const props = defineProps({
  isPageLoading: {
    type: Boolean,
  },
});
const emit = defineEmits(['sendKeywordParams']);

const route = useRoute();

const store = useAnalysisStore();
const dataStore = useAnalysisDataStore();

const { sajuData, sData } = storeToRefs(dataStore);

const isReadyToShow = computed(() => !props.isPageLoading && !isObjectEmpty(sData.value));

const guestId = route.params.id;
const guestIdRef = computed(() => guestId);
const { ratingResult, ratingStatus, getRate, postRate } = useContentRate('SAJU_INTERPRETATION', guestIdRef);

// 데이터 요청 파라미터
const sajuDescParams = computed(() => {
  if (isObjectEmpty(sajuData.value)) {
    return null;
  }

  return {
    guestUserId: guestId,
    lang: 'KOR',
    birth: sajuData.value.solar ?? null,
    userName: sajuData.value.inputdata.name ?? store.userName,
    bln: sajuData.value.transdata.lucknum ?? null,
    gender: Number(sajuData.value.inputdata.gender) ?? null,
    unknownTime: sajuData.value.inputdata.unknown_time ?? null,
    analysisTypeId: 8,
  };
});

// 인덱스
const indexNo = ref(1);
/** 페이지 이동 (로그 없이) */
const moveToPage = (id) => {
  indexNo.value = id;
};

/** index값으로 페이지 이동 (목차 클릭 또는 페이지네이션) */
const updateIndexNo = (id, tocIdx) => {
  if (tocIdx != null) {
    // 목차 클릭 (사주풀이는 무료 전용이라 free). 신규 type — DB 등록 후 작동
    addContentLogAPI({
      contentType: 'as_summary_toc_free_opt3',
      option3: String(tocIdx + 1),
    });
  } else {
    // 페이지 이전/다음 이동: 목차 클릭이 아니므로 index_opt3 오발화 제거, 신규 page_opt3 발화
    addContentLogAPI({
      contentType: 'as_summary_page_opt3',
      option3: String(id),
    });
  }
  moveToPage(id);
};

watch(
  () => store.selectedContentIndex,
  (newIdx) => {
    const target = CONTENTS_PAGE_MAP[newIdx];
    if (target) moveToPage(target);
  },
  { immediate: true },
);

/** 표지로 이동 */
const goToOverView = () => {
  store.clickMainMenu('/overviewSajuSummary');
};

onMounted(async () => {
  if (!isObjectEmpty(sajuData.value) && isObjectEmpty(sData.value)) {
    fetchSajuDescData();
  }
  getRate();
});

watch(sajuData, (newData) => {
  if (!isObjectEmpty(newData) && isObjectEmpty(sData.value)) {
    fetchSajuDescData();
  }
  getRate();
});

/** 사주풀이 데이터 요청 */
const fetchSajuDescData = async () => {
  try {
    const result = await fetchBaseServiceAPI(sajuDescParams.value);
    dataStore.setSData(result.data);
    emit('sendKeywordParams', result.data?.['용어해석']);
  } catch (error) {
    DEV_MODE && console.error('fetchSajuDescData 실패,', error);
  }
};

</script>

<template>
  <div v-if="store.selectedMenu.endsWith('summary')" class="ss-full-height">
    <div v-if="isPageLoading" class="analysis__loading-overlay">
      <div class="loading-olverlay__img"></div>
      상세 분석중입니다!
    </div>

    <ContentsBasic01 v-if="indexNo === 1" />
    <ContentsBasic02 v-if="indexNo === 2" />
    <ContentsBasic03 v-if="indexNo === 3 && isReadyToShow" />
    <ContentsBasic04 v-if="indexNo === 4 && isReadyToShow" @updateIndexNo="updateIndexNo" />
    <ContentsBasic05 v-if="indexNo === 5 && isReadyToShow" />
    <ContentsBasic06 v-if="indexNo === 6 && isReadyToShow" />
    <ContentsBasic07 v-if="indexNo === 7 && isReadyToShow" />
    <ContentsBasic08 v-if="indexNo === 8 && isReadyToShow" />
    <ContentsBasic09
      v-if="indexNo === 9 && isReadyToShow"
      :ratingResult="ratingResult"
      :ratingStatus="ratingStatus"
      @postRate="postRate"
      @goToOverView="goToOverView"
    />
  </div>

  <div class="contentsListBtnBox">
    <ContentsList
      :items="CONTENTS"
      :curPage="indexNo"
      :lastPage="9"
      @updateIndexNo="updateIndexNo"
      @goToOverView="goToOverView"
    />
  </div>
</template>

<style lang="scss">
@import '/src/assets/nangman_style/personality.scss';
</style>
